import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Form, Spinner } from "react-bootstrap";
import { FaCog } from "react-icons/fa";
import api, { BASE_URL } from "../../Utils/api";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import formatarData from "../../Utils/formatarData";

const PARAMETROS_META = {
  mostrar_ids_exclusao: {
    label: "Mostrar IDs na validação de exclusão",
    descricaoUI:
      "Quando ativo, as mensagens de bloqueio de exclusão exibem os IDs dos registros vinculados. " +
      "Quando inativo, a mensagem é genérica (indica apenas o componente, sem expor IDs internos).",
    tipo: "boolean",
  },
  mostrar_coluna_id: {
    label: "Mostrar coluna ID nas grids",
    descricaoUI:
      "Quando ativo, a coluna ID é exibida nas grids padrão do sistema. " +
      "Quando inativo, a coluna é ocultada para uma visualização mais limpa.",
    tipo: "boolean",
  },
};

function valorParaBoolean(valor) {
  return String(valor).toUpperCase() === "S";
}

function booleanParaValor(bool) {
  return bool ? "S" : "N";
}

function ParametroSwitch({ parametro, onToggle, salvando }) {
  const meta = PARAMETROS_META[parametro.chave] || {};
  const ativo = valorParaBoolean(parametro.valor);

  return (
    <tr>
      <td className="align-middle fw-semibold" style={{ width: "30%" }}>
        <div className="d-flex align-items-center gap-2">
          <FaCog size={14} className="text-secondary" />
          {meta.label || parametro.chave}
        </div>
        <div className="text-muted small fw-normal font-monospace mt-1">
          {parametro.chave}
        </div>
      </td>
      <td className="align-middle text-center" style={{ width: "12%" }}>
        <div className="d-flex justify-content-center">
          <Form.Check
            type="switch"
            id={`switch-${parametro.chave}`}
            checked={ativo}
            onChange={() => onToggle(parametro.chave, !ativo)}
            disabled={salvando === parametro.chave}
            label=""
            className="m-0"
          />
        </div>
      </td>
      <td className="align-middle text-muted small" style={{ width: "33%" }}>
        {meta.descricaoUI || parametro.descricao || "—"}
      </td>
      <td className="align-middle text-muted small text-nowrap" style={{ width: "15%" }}>
        {parametro.atualizado_em ? formatarData(parametro.atualizado_em) : "—"}
      </td>
    </tr>
  );
}

function ParametrosGodMode() {
  const { toast } = useFeedback();
  const token = localStorage.getItem("authToken");

  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(null);

  const fetchParametros = useCallback(() => {
    setLoading(true);
    api
      .get(`${BASE_URL}/api/godmode/parametros`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setParametros(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => toast.error("Erro ao carregar parâmetros."))
      .finally(() => setLoading(false));
  }, [token, toast]);

  useEffect(() => { fetchParametros(); }, [fetchParametros]);

  const handleToggle = (chave, novoBoolean) => {
    const novoValor = booleanParaValor(novoBoolean);

    setParametros((prev) =>
      prev.map((p) => (p.chave === chave ? { ...p, valor: novoValor } : p))
    );
    setSalvando(chave);

    api
      .put(
        `${BASE_URL}/api/godmode/parametros/${chave}`,
        { valor: novoValor },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => toast.success("Parâmetro atualizado com sucesso."))
      .catch((err) => {
        toast.error(err.response?.data?.message || "Erro ao salvar parâmetro.");
        setParametros((prev) =>
          prev.map((p) =>
            p.chave === chave ? { ...p, valor: booleanParaValor(!novoBoolean) } : p
          )
        );
      })
      .finally(() => setSalvando(null));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Carregando parâmetros...</span>
      </div>
    );
  }

  return (
    <div>
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-dark text-white d-flex align-items-center gap-2 py-3">
          <FaCog />
          <strong>Parâmetros do Sistema</strong>
          <span className="ms-auto badge bg-secondary">GOD MODE</span>
        </Card.Header>
        <Card.Body className="p-0">
          {parametros.length === 0 ? (
            <div className="text-muted text-center py-5">
              Nenhum parâmetro configurado.
              <div className="small mt-1">
                Execute o script <code>sql/godmode_parametros.sql</code> no banco de dados.
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Parâmetro</th>
                    <th className="text-center">Ativo</th>
                    <th>Descrição</th>
                    <th>Última alteração</th>
                  </tr>
                </thead>
                <tbody>
                  {parametros.map((p) => (
                    <ParametroSwitch
                      key={p.chave}
                      parametro={p}
                      onToggle={handleToggle}
                      salvando={salvando}
                    />
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="text-muted small px-1">
        <strong>Atenção:</strong> alterações nesta tela têm efeito imediato em todo o sistema e
        são visíveis apenas para usuários com permissão <em>Super Admin</em>.
      </div>
    </div>
  );
}

export default ParametrosGodMode;
